import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Spinner,
  Badge,
} from "reactstrap";
import { updateComplaints } from "./supportApi";

const EditComplaintModal = ({ isOpen, toggle, complaints, onUpdate }) => {
  const [state, setState] = useState("");
  const [category, setCategory] = useState("");
  const [sideNote, setSideNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [originalSideNote, setOriginalSideNote] = useState("");

  const isBulk = complaints && complaints.length > 1;

  useEffect(() => {
    if (isOpen && complaints && complaints.length > 0) {
      // For single complaint, pre-fill with current values
      if (!isBulk && complaints[0]) {
        setState(complaints[0].state || "");
        setCategory(complaints[0].category || "");
        const currentSideNote = complaints[0].sideNote || "";
        setSideNote(currentSideNote);
        setOriginalSideNote(currentSideNote);
      } else {
        // For bulk, reset to empty
        setState("");
        setCategory("");
        setSideNote("");
        setOriginalSideNote("");
      }
    }
  }, [isOpen, complaints, isBulk]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!complaints || complaints.length === 0) {
      alert("No complaints selected");
      return;
    }

    // Check if at least one field is being changed
    const hasStateChange = state && state !== "";
    const hasCategoryChange = category && category !== "";
    const hasSideNoteChange = isBulk 
      ? (sideNote !== "" && sideNote.trim() !== "") // For bulk, empty means keep current
      : (sideNote !== originalSideNote); // For single, check if changed from original

    if (!hasStateChange && !hasCategoryChange && !hasSideNoteChange) {
      alert("Please provide at least one field to update (state, category, or side note)");
      return;
    }

    setLoading(true);
    try {
      const complaintIds = complaints.map((c) => c.id || c);

      const updateData = {
        complaint_id: complaintIds,
      };

      if (state) updateData.state = state;
      if (category) updateData.category = category;
      // For bulk: only send if not empty (to update all selected)
      // For single: send if changed from original (including if cleared to empty string)
      if (isBulk) {
        if (sideNote && sideNote.trim()) {
          updateData.sideNote = sideNote.trim();
        }
      } else {
        // For single edit, send sideNote if it's different from original
        // This includes clearing it (empty string when original had value)
        if (sideNote !== originalSideNote) {
          updateData.sideNote = sideNote.trim();
        }
      }

      await updateComplaints(updateData);

      if (onUpdate) {
        onUpdate();
      }
      toggle();
      // Reset form
      setState("");
      setCategory("");
      setSideNote("");
    } catch (error) {
      console.error("Error updating complaints:", error);
      alert(error.response?.data?.message || error.message || "Failed to update complaints");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setState("");
    setCategory("");
    setSideNote("");
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg">
      <form onSubmit={handleSubmit}>
        <ModalHeader toggle={handleClose} className="bg-primary text-white">
          <i className="mdi mdi-pencil me-2"></i>
          {isBulk ? "Bulk Edit Complaints" : "Edit Complaint"}
        </ModalHeader>
        <ModalBody>
          {isBulk && (
            <div className="alert alert-info mb-3">
              <i className="mdi mdi-information me-2"></i>
              Updating <Badge color="primary">{complaints.length}</Badge> selected complaint{complaints.length !== 1 ? 's' : ''}.
              Leave fields empty to keep current values.
            </div>
          )}

          {!isBulk && complaints && complaints[0] && (
            <div className="mb-3">
              <p className="mb-1">Editing Complaint ID: <strong>{complaints[0].id}</strong></p>
              {complaints[0].user && (
                <small className="text-muted">User: {complaints[0].user.username} ({complaints[0].user.userId})</small>
              )}
            </div>
          )}

          <FormGroup>
            <Label>State</Label>
            <Input
              type="select"
              value={state}
              onChange={(e) => setState(e.target.value)}
            >
              <option value="">-- Keep Current --</option>
              <option value="raised">Raised</option>
              <option value="inProgress">In Progress</option>
              <option value="testing">Testing</option>
              <option value="done">Done</option>
            </Input>
            <small className="text-muted">Select a state to update, or leave as "Keep Current"</small>
          </FormGroup>

          <FormGroup>
            <Label>Category</Label>
            <Input
              type="select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">-- Keep Current --</option>
              <option value="UI/UX Issues">UI/UX Issues</option>
              <option value="Feature Not Working">Feature Not Working</option>
              <option value="App Not Working Properly">App Not Working Properly</option>
              <option value="New Feature Request">New Feature Request</option>
              <option value="Account & Profile Issues">Account & Profile Issues</option>
              <option value="Notifications & Alerts Issues">Notifications & Alerts Issues</option>
              <option value="Third-Party Integrations Issues">Third-Party Integrations Issues</option>
              <option value="Feedback & Suggestions">Feedback & Suggestions</option>
              <option value="Others">Others</option>
              <option value="Not Defined">Not Defined</option>
            </Input>
            <small className="text-muted">Select a category to update, or leave as "Keep Current"</small>
          </FormGroup>

          <FormGroup>
            <Label>Side Note</Label>
            <Input
              type="textarea"
              rows="4"
              value={sideNote}
              onChange={(e) => setSideNote(e.target.value)}
              placeholder="Enter side note..."
            />
            <small className="text-muted">
              {isBulk 
                ? "Enter side note text to apply to all selected complaints. Leave empty to keep current values."
                : "Enter or update the side note for this complaint. Clear the field to remove the side note."
              }
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              <>
                <i className="mdi mdi-check me-2"></i>Update
              </>
            )}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default EditComplaintModal;

